import { Capacitor } from "@capacitor/core";
import { motion } from "framer-motion";
import { useCallback, useEffect, useState } from "react";
import {
  LoaderFunction,
  useLoaderData,
  useNavigate,
  useSearchParams,
} from "react-router-dom";

import { RepeatMDFooterV3 } from "@/atoms/repeatmd-footer-v3/RepeatMDFooterV3";
import { PlatformLogo } from "@/components/PlatformLogo";
import { ANIMATION_DURATION } from "@/constants/animations.constants";
import { AnonymousLogin } from "@/modules/onboarding/components/AnonymousLogin/AnonymousLogin";
import { MobileViewportContainer } from "@/modules/onboarding/components/MobileViewportContainer/MobileViewportContainer";
import SignUpPerks from "@/modules/onboarding/components/SignUpPerks/SignUpPerks";
import { LoginOrSignUpForm } from "@/modules/onboarding/forms/LoginOrSignUpForm/LoginOrSignUpForm";
import { LocalStorageService } from "@/modules/storage";
import { queryClient } from "@/pages/Root";
import Services from "@/services/Services";
import { waitObj } from "@/toolbox/Promise";
import { get, post } from "api";

type ExistsResponse = {
  exists: boolean;
  isRegistrationPending?: boolean;
};

type LoginResponse = {
  success: boolean;
  message?: string;
};

const load = async ({ url }: Request) => {
  const urlObj = new URL(url);
  let referralCode = urlObj.searchParams.get("referralCode");
  const checkInCode = urlObj.searchParams.get("checkInCode");

  if (!referralCode) {
    referralCode = LocalStorageService.getItem("referralCode");
  }

  let statelessReferralCode = urlObj.searchParams.get("statelessReferralCode");

  if (!statelessReferralCode) {
    statelessReferralCode = LocalStorageService.getItem(
      "statelessReferralCode"
    );
  }

  const localStoragePlatformId = LocalStorageService.getItem("platformId");

  const theme = queryClient.fetchQuery(["theme"], () =>
    !localStoragePlatformId ? get("/platform/theme") : null
  );

  const multiPlatformTheme = queryClient.fetchQuery(
    [
      "/multi-platform/platform/{platformId}/theme",
      { platformId: localStoragePlatformId },
    ],
    () =>
      localStoragePlatformId
        ? get("/multi-platform/platform/{platformId}/theme", {
            platformId: LocalStorageService.getItem("platformId") || "",
          })
        : null
  );

  const isAuthorized = Services.auth.isAuthorized();
  const platform = queryClient.fetchQuery(["/platform/current"], () =>
    get("/platform/current").catch(() => null)
  );

  return waitObj({
    referralCode,
    statelessReferralCode,
    theme,
    multiPlatformTheme,
    isAuthorized,
    platform,
    checkInCode,
  });
};

type LoaderData = Awaited<ReturnType<typeof load>>;
export const v3SignInDisplayLoader: LoaderFunction = ({ request }) => {
  return load(request);
};

const clearGiftCardData = () => {
  LocalStorageService.removeItem("isGiftCardClaim");
  LocalStorageService.removeItem("giftCardTransferId");
  LocalStorageService.removeItem("giftCardRecipientPhone");
  LocalStorageService.removeItem("giftCardRecipientPlatformUserId");
  LocalStorageService.removeItem("giftCardImageUrl");
  LocalStorageService.removeItem("giftCardAmount");
  LocalStorageService.removeItem("referralSenderName");
  LocalStorageService.removeItem("giftCardInfo");
};

const V3SignInPage = () => {
  const { theme, multiPlatformTheme, platform, isAuthorized } =
    (useLoaderData() as LoaderData) || {};
  const navigate = useNavigate();
  // decides if we show the checkbox to confirm ToS
  const [isNewUser, setIsNewUser] = useState(false);
  const [searchParams] = useSearchParams();
  const comingFromDeepLink = !!LocalStorageService.getItem("platformId");

  useEffect(() => {
    const checkInCode = searchParams.get("checkInCode");
    const referralCode = searchParams.get("referralCode");
    const platformId = searchParams.get("platformId");

    if (referralCode) {
      LocalStorageService.setItem("referralCode", referralCode);
    }

    if (checkInCode) {
      LocalStorageService.setItem("scannedCode", checkInCode);
    }

    if (platformId) {
      LocalStorageService.setItem("platformId", platformId);
    }
  }, [searchParams]);

  // Check if coming from gift card claim flow
  const isGiftCardClaim =
    LocalStorageService.getItem("isGiftCardClaim") === "true";
  const giftCardRecipientPhone =
    LocalStorageService.getItem("giftCardRecipientPhone") || "";
  // Format phone number for display (remove +1 and format as (XXX) XXX-XXXX)
  const initialPhone =
    isGiftCardClaim && giftCardRecipientPhone
      ? giftCardRecipientPhone.replace("+1", "")
      : "";

  const proceedToVerification = useCallback(
    (normalizedPhone: string, isNew: boolean) => {
      const state = {
        phone: normalizedPhone,
        newUser: isNew,
        signupReferralCode:
          LocalStorageService.getItem("referralCode") || undefined,
        statelessReferralCode:
          LocalStorageService.getItem("statelessReferralCode") || undefined,
      } as const;
      // TODO local stoarge keys should be very specific
      // state could mean many things and could be overwitten by other flows
      LocalStorageService.setItem("v3SignInState", JSON.stringify(state));
      navigate("/signin/verification", { state });
    },
    [navigate]
  );

  const sendLogin = useCallback(async (normalizedPhone: string) => {
    const loginResponse = (await post("/auth/login", {
      phoneNumber: normalizedPhone,
    })) as LoginResponse;
    if (!loginResponse.success) {
      throw new Error(
        loginResponse.message || "Unable to send verification code."
      );
    }
  }, []);

  const onPhoneNumberSubmitted = useCallback(
    async (formattedPhoneNumber: string) => {
      setIsNewUser(false);
      const normalizedPhone = "+1" + formattedPhoneNumber.replace(/[^\d]/g, "");

      // Check if phone number matches gift card recipient
      // If user changed the phone number, clear gift card data and proceed with normal registration
      let shouldClaimGiftCard = isGiftCardClaim;
      if (isGiftCardClaim && giftCardRecipientPhone) {
        if (normalizedPhone !== giftCardRecipientPhone) {
          // Clear all gift card related data from storage
          clearGiftCardData();
          shouldClaimGiftCard = false;
        }
      }

      const existsApiPath = Capacitor.isNativePlatform()
        ? "/multi-platform/auth/exists"
        : "/auth/exists";

      const existingPlatformId = LocalStorageService.getItem("platformId");
      let userExistsResponse: ExistsResponse | undefined;
      try {
        userExistsResponse = (await get(existsApiPath, {
          phoneNumber: normalizedPhone,
          ...(existingPlatformId && { platformId: existingPlatformId }),
        })) as ExistsResponse;
      } catch (err) {
        // TODO this should not be silent failure
        // If existence check fails (network/404), assume new user and show consent step
        setIsNewUser(true);
        return;
      }

      // If claiming a gift card, treat as new user even if they exist
      // to force them through the V3 onboarding flow
      if (shouldClaimGiftCard) {
        setIsNewUser(true);
        return;
      }

      if (
        userExistsResponse &&
        userExistsResponse.exists &&
        !userExistsResponse.isRegistrationPending
      ) {
        await sendLogin(normalizedPhone);
        proceedToVerification(normalizedPhone, false);
      } else {
        // Treat as new user: require SMS agreement before sending code
        setIsNewUser(true);
      }
    },
    [sendLogin, proceedToVerification, isGiftCardClaim, giftCardRecipientPhone]
  );

  const handleSendVerificationForNewUser = useCallback(
    async (formattedPhoneNumber: string) => {
      const normalizedPhone = "+1" + formattedPhoneNumber.replace(/[^\d]/g, "");

      await sendLogin(normalizedPhone);
      proceedToVerification(normalizedPhone, true);
    },
    [sendLogin, proceedToVerification]
  );

  const onSubmitHandler = isNewUser
    ? handleSendVerificationForNewUser
    : onPhoneNumberSubmitted;

  const logoUrl = Capacitor.isNativePlatform()
    ? comingFromDeepLink
      ? multiPlatformTheme?.logoImageUrl
      : null
    : theme?.platformLogoUrl;

  return (
    <MobileViewportContainer contentClassName="pt-0 flex flex-col justify-between min-h-[calc(100vh-env(safe-area-inset-top)-env(safe-area-inset-bottom))]">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: ANIMATION_DURATION, delay: 0.1 }}
        className="flex-1"
      >
        <LoginOrSignUpForm
          initialPhoneNumber={initialPhone}
          onSubmit={onSubmitHandler}
          consentRequired={isNewUser}
          submitLabel={isNewUser ? "Send verification code" : undefined}
          prefix={<PlatformLogo logoUrl={logoUrl} className="mb-14" />}
          suffix={
            <SignUpPerks
              rewardsEnabled={
                platform?.platformSettings?.rewardsEnabled ?? true
              }
              spendBasedRewardsEnabled={
                platform?.platformSettings?.spendBasedRewardsEnabled ?? true
              }
              className="mt-8"
              animate
            />
          }
        />
      </motion.div>
      <div className="mt-auto">
        <AnonymousLogin
          isAuthorized={!!isAuthorized}
          xsText
          className="mx-auto"
        />
        <RepeatMDFooterV3 showLinks />
      </div>
    </MobileViewportContainer>
  );
};

export default V3SignInPage;
